// material-ui
import { styled } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';

// project-imports
import { DRAWER_WIDTH, MINI_DRAWER_WIDTH, ThemeMode } from 'config';

// Sidebar chrome color is intentionally fixed (not palette-driven) so it stays a
// consistent professional navy regardless of the app's light/dark content theme.
export const SIDEBAR_BG = '#0F1B2E';
export const SIDEBAR_BORDER = 'rgba(255, 255, 255, 0.08)';

// Receptionist role gets a distinct maroon sidebar for the whole session, so it's obvious at a
// glance (especially deep inside the frontdesk check-in flow) which mode they're in.
export const RECEPTIONIST_SIDEBAR_BG = '#6E1A37';

// Gold pairs naturally with the maroon above (and already matches the SUMADHURA wordmark and the
// existing collapse-item hover accent), so nav hover/selected states and the Check-In button all
// switch to it for Receptionist instead of the app's default blue primary accent.
export const RECEPTIONIST_ACCENT = '#C79A42';

// Opaque light cream for the Check-In pill's resting state — a low-alpha tint of the maroon
// itself is nearly invisible against the maroon rail it sits on, so this needs to be a genuinely
// light, opaque surface (mirroring how the default theme's `primary.lighter` is opaque too).
export const RECEPTIONIST_SURFACE = '#F7E9CF';

// Case-insensitive since the backend doesn't always send the role string with consistent casing.
export const isReceptionist = () => localStorage.getItem('role')?.trim().toLowerCase() === 'receptionist';

// Security only ever uses the Check-in page (see CheckInType.jsx/generateopt.js) — the sidebar's
// full nav tree has nothing else relevant for them, so MainLayout hides it outright for this role.
export const isSecurity = () => localStorage.getItem('role')?.trim().toLowerCase() === 'security';
// export const isReceptionist = () => {
//   const role = localStorage.getItem('role')?.trim().toLowerCase();

//   return ['receptionist', 'admin', 'management fd'].includes(role);
// };
// Persisted at login from the login response's own `office_types` (see JWTContext.js's login()) —
// lets dashboard routing recognize ANY role whose office types are Front-Office-only WITHOUT
// hardcoding that role's name. Without this, a newly created role (e.g. "Management FD") that
// isn't literally named "Receptionist" would fall through to the generic dashboard even though its
// office type is Front Office, same as Receptionist's.
export const getOfficeTypeNames = () => {
  try {
    const stored = JSON.parse(localStorage.getItem('officeTypeNames') || '[]');
    return Array.isArray(stored) ? stored.map((name) => name.trim().toLowerCase()) : [];
  } catch {
    return [];
  }
};

export const isFrontOfficeOnly = () => {
  const names = getOfficeTypeNames();
  return names.length > 0 && names.every((name) => name === 'front office');
};

// The single source of truth for "should this session look/feel like Front Desk" (maroon/gold
// sidebar, Check-In button styling, nav accent color) — covers both the Receptionist role by name
// (today's only such role) AND any future role whose office_types are Front-Office-only, so a
// newly created role never needs a frontend code change to pick up the right theme.
export const isFrontDeskThemed = () => isReceptionist() || isFrontOfficeOnly();

// Exported so the top Header AppBar (see ../Header/index.js) can match this instead of staying
// hardcoded navy — sidebar + header need to read as one consistent chrome surface for every role.
export const getSidebarBg = () => (isFrontDeskThemed() ? RECEPTIONIST_SIDEBAR_BG : SIDEBAR_BG);

const openedMixin = (theme) => ({
  background: getSidebarBg(),
  width: DRAWER_WIDTH,
  borderRight: `1px solid ${SIDEBAR_BORDER}`,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: 'hidden',
  boxShadow: theme.palette.mode === ThemeMode.DARK ? theme.customShadows.z1 : 'none'
});

const closedMixin = (theme) => ({
  background: getSidebarBg(),
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: 'hidden',
  width: MINI_DRAWER_WIDTH,
  borderRight: 'none',
  boxShadow: theme.customShadows.z1
});

// ==============================|| DRAWER - MINI STYLED ||============================== //

const MiniDrawerStyled = styled(Drawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  width: DRAWER_WIDTH,
  height: 100,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme)
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme)
  })
}));

export default MiniDrawerStyled;
